import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/contexts/AuthContext";
import { AuthShell, TextField } from "@/pages/Login";
import { Lock, CheckCircle, WarningCircle } from "@phosphor-icons/react";

export default function PasswortZuruecksetzenPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <AuthShell>
        <div className="text-center" data-testid="password-reset-invalid-token">
          <div className="w-14 h-14 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-5">
            <WarningCircle size={30} weight="duotone" className="text-red-600" />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-forest-900">
            Ungültiger Link
          </h1>
          <p className="mt-3 font-body text-[15px] text-[#4A524C]">
            Der Link zum Zurücksetzen scheint unvollständig zu sein. Bitte fordere einen neuen an.
          </p>
          <Link
            to="/passwort-vergessen"
            className="mt-6 inline-flex items-center gap-2 text-forest-700 font-medium hover:underline"
          >
            → Neuen Link anfordern
          </Link>
        </div>
      </AuthShell>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Das Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    if (password !== confirm) {
      setError("Die beiden Passwörter stimmen nicht überein.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/confirm`, {
        token,
        new_password: password,
      });
      setDone(true);
      toast.success("Passwort erfolgreich geändert");
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Zurücksetzen fehlgeschlagen. Der Link ist möglicherweise abgelaufen.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthShell>
        <div className="text-center" data-testid="password-reset-success">
          <div className="w-14 h-14 rounded-full bg-forest-50 border border-forest-100 flex items-center justify-center mx-auto mb-5">
            <CheckCircle size={30} weight="duotone" className="text-forest-700" />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-forest-900">
            Passwort geändert
          </h1>
          <p className="mt-3 font-body text-[15px] text-[#4A524C]">
            Du wirst gleich zum Login weitergeleitet …
          </p>
          <Link to="/login" className="mt-5 inline-block text-sm text-forest-700 font-medium hover:underline">
            Zum Login
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-forest-900">
        Neues Passwort vergeben
      </h1>
      <p className="mt-2 font-body text-[15px] text-[#4A524C]">
        Wähle ein sicheres Passwort mit mindestens 8 Zeichen.
      </p>

      <form onSubmit={onSubmit} data-testid="password-reset-confirm-form" className="mt-8 space-y-5">
        <TextField
          label="Neues Passwort"
          name="password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
          testId="password-reset-new-input"
        />
        <TextField
          label="Passwort bestätigen"
          name="confirm"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
          minLength={8}
          testId="password-reset-confirm-input"
        />
        {error && (
          <div
            data-testid="password-reset-confirm-error"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          >
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="password-reset-confirm-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-forest-600 hover:bg-forest-700 disabled:opacity-60 text-white font-heading font-medium px-6 py-3 rounded-xl transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5"
        >
          <Lock size={18} weight="bold" />
          {loading ? "Speichere …" : "Passwort ändern"}
        </button>
      </form>

      <p className="mt-7 font-body text-sm text-[#4A524C]">
        <Link to="/login" className="text-forest-700 font-medium hover:underline">
          ← Zurück zum Login
        </Link>
      </p>
    </AuthShell>
  );
}
